import CalendarControl from 'scoreboard/components/calendar-control'
import _ from 'lodash'
import moment from 'moment'
import momentHelper from 'scoreboard/components/moment-helper'
import MultiSelector from 'scoreboard/components/multi-selector'

/**
 * The date-multi-selector brings together a scoreboard-select and a
 * calendar-control for issue #12990
 */
var DateMultiSelector = MultiSelector.extend({

	className: MultiSelector.prototype.className + ' date-multi-selector',

	_setupControl() {
		this._setDates(this.options.dates)

		// This calendar is used as the only `li` element in the rich select menu's _resultset
		this.calendarControl = new CalendarControl({
			tooltip: false,
			required: false,
			autoHideOnClick: false, // Allow the rich select to handle events
			autoHideOnBlur: false, // Same as above
			invalidate: false
		}).render()
	},

	_setupListeners() {
		// Set up some things to control rome a bit
		this.calendarControl.rome.addMonth = () => {
			this.calendarControl.rome.changeMonth('add', true) // set silent to true
			this.calendarControl.updateDropdownView() // So the dropdown changes
		}
		this.calendarControl.rome.subtractMonth = () => {
			this.calendarControl.rome.changeMonth('subtract', true) // set silent to true
			this.calendarControl.updateDropdownView()
		}

		// Because we're overriding the rome month changes to prevent emitting events
		// we can count on the `data` event meaning the user clicked a date.
		// Changing the month/year dropdowns from our calendar-control uses rome.setValue
		// which doesn't trigger `data` events either, which is what we want.
		this.calendarControl.rome.on('data', date => {
			if (this._rejectDuplicates(date)) {
				return
			}

			// Check if there's an active tag. If so we need to adjust that object
			var activeIdx = this.selectOptions.findIndex(d => d.active)

			if (activeIdx !== -1) {
				// Remove the current active date since they changed it
				this.selectOptions.splice(activeIdx, 1)
			} else {
				// Fresh date selected, we'll throw it at the end of the array
				activeIdx = this.selectOptions.length
			}

			// Update our dates
			this.selectOptions.splice(activeIdx, 0, {
				selected: true,
				label: momentHelper.formatDate(date),
				value: this.calendarControl.getIsoDate(),
				dataset: {
					type: 'date'
				}
			})

			this.renderSelect(false)
			this._triggerSelectEvent()
		})
	},

	_compareResults(oldVal, newVal) {
		return moment(oldVal).isSame(moment(newVal))
	},

	/*
	 * Draws the _resultset of the rich selection menu. We override the rich select _resultset
	 * renderer to draw a rome calendar selector. Extracted here so it can be overriden by subclasses
	 */
	_resultset: function (selection) {
		var calendar = this.calendarControl
		var activeTag = this.activeTag()
		var li = selection.selectAll('li').data(d => [d]) // Rebind to the entire array

		li.enter()
			.append('li')
			.classed('calendar-container', true)
			.select(function () {
				this.appendChild(calendar.el)
				return this
			})
			.select('.rd-container')
			.style('display', 'inline-block') // Make sure rome is showing itself. There's probably a better way but rome's API is horrible

		li.exit()
			.remove()

		// So we can prevent defaulting to the current day in rome, make sure the
		// .calendar-container is classed correctly with 'calendar-container-no-active-day'
		// based on whether there is an active tag or not. Doing this in d3 with
		// .classed() doesn't work for some reason so doing it here in vanilla js.
		let calendarContainer = selection.nodes()[0].querySelector('.calendar-container')
		if (activeTag) {
			calendarContainer.classList.remove('calendar-container-no-active-day')
			// The user is changing the selected date so make sure the calendar is accurate
			calendar.updateDropdownView(activeTag.value)
			calendar.rome.setValue(moment(activeTag.value))
		} else {
			calendarContainer.classList.add('calendar-container-no-active-day')
		}
	},

	_setDates(dates) {
		let selectOptions = (dates || []).map(date => {
			return {
				selected: true,
				label: momentHelper.formatDate(date),
				value:  _.isNil(date) ? date : momentHelper.isoDate(moment(date)),
				dataset: {
					type: 'date'
				}
			}
		})
		// Sort the dates in chronological order
		selectOptions.sort((a, b) => a.value - b.value)

		this.selectOptions = selectOptions
	},

	_val(dates) {
		if (_.isUndefined(dates)) {
			return _.map(this.$el.val(), momentHelper.isoDate)
		} else {
			this._setDates(dates)
			this.renderSelect()
		}
	},

	remove: function() {
		this.calendarControl.remove()
		MultiSelector.prototype.remove.apply(this, arguments)
	}

})

export default DateMultiSelector

DateMultiSelector.__filename__ = 'components/date-multi-selector.js'
